import { useMemo, useState } from 'react';
import { Button, ConfirmDialog } from '@mlm/ui';
import { diffConfig, RateDiffPreview } from './diff-summary';

/**
 * A Save button that gates the save behind a confirmation dialog showing a
 * field-by-field diff of the change plus a type-to-confirm phrase. Used for
 * tenant-settings saves (which overwrite a single mutable config row) so the
 * admin reviews exactly what changes before committing. The change trail is
 * recorded in audit_log server-side.
 *
 * `current` and `next` are the objects to diff (pass the relevant slice so the
 * preview shows only this page's changes). `onConfirm` runs the actual mutation
 * (use the mutation's `mutateAsync`); on resolve the dialog closes.
 */
export function ConfirmSaveButton({
  current,
  next,
  onConfirm,
  label = 'Save changes',
  title = 'Save settings',
  description = 'Review the changes below, then confirm. This overwrites the current settings.',
  confirmPhrase = 'SAVE SETTINGS',
  loading = false,
  disabled = false,
  size,
  errorMessage,
  validate,
  onInvalid,
}: {
  current: unknown;
  next: unknown;
  onConfirm: () => Promise<unknown>;
  label?: string;
  title?: string;
  description?: React.ReactNode;
  confirmPhrase?: string;
  loading?: boolean;
  disabled?: boolean;
  size?: React.ComponentProps<typeof Button>['size'];
  errorMessage?: string | null;
  /** Optional pre-open validation; return a message to block opening. */
  validate?: () => string | null;
  /** Surfaces a validation message in the caller's own error UI. */
  onInvalid?: (msg: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => (open ? diffConfig(current ?? {}, next ?? {}) : []), [open, current, next]);

  function onTrigger() {
    if (validate) {
      const msg = validate();
      if (msg) {
        onInvalid?.(msg);
        return;
      }
    }
    setOpen(true);
  }

  return (
    <>
      <Button onClick={onTrigger} disabled={disabled || loading} size={size}>
        {loading ? 'Saving…' : label}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={title}
        description={description}
        confirmLabel={label}
        loading={loading}
        errorMessage={errorMessage}
        typedConfirm={{ expected: confirmPhrase }}
        onConfirm={async () => {
          await onConfirm();
          setOpen(false);
        }}
      >
        <RateDiffPreview rows={rows} />
      </ConfirmDialog>
    </>
  );
}
